/**
 * Shared motion + interaction primitives for the live layer (design spec
 * §4.1). The motion hooks and `DISMISS_BUTTON_CLASSES` are internal to
 * `src/components/live/` — surfaces compose the components, never these
 * helpers directly. The two tap-treatment constants (`PRESS_FEEDBACK_CLASSES`,
 * `FOCUS_RING_CLASSES`) ARE barrel-exported: live-layer tappables outside this
 * directory (e.g. the space hero) wear the same press/focus feel.
 */
import { useEffect, useRef, useState } from 'react';
import type { TransitionEvent } from 'react';

/**
 * Press feedback for every live-layer tappable (§4.1): scale to 0.96 over
 * 160ms ease-out. `transition-transform` covers Tailwind v4's standalone
 * `scale` property; never `transition: all`.
 */
export const PRESS_FEEDBACK_CLASSES =
  'active:scale-[0.96] transition-transform duration-[160ms] ease-out';

/** House focus treatment for custom (non-shadcn) interactive elements. */
export const FOCUS_RING_CLASSES =
  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2';

/**
 * The small ✕: a true 44px hit area (`InviteBanner` pattern — real h-11/w-11
 * box around a small icon, call sites add negative margins to stay visually
 * slim). Being a flex sibling of the body/CTA keeps hit areas non-overlapping.
 */
export const DISMISS_BUTTON_CLASSES = `flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 ${PRESS_FEEDBACK_CLASSES} ${FOCUS_RING_CLASSES}`;

/**
 * Blur-masked swap / cross-fade motion (design spec §4.1): opacity +
 * blur(4px→0) + scale(0.97→1) at 200ms `ease-swap`, the blur bridging the
 * seam so two overlapping states read as one morph. Reduced motion keeps the
 * opacity fade only (drops the blur + scale movement). One shared token so
 * `SpaceIndicator`'s "at {event}" cross-fade and `LiveSlotContentRow`'s
 * rotating content row render byte-identical motion — the same visual
 * language in two surfaces, not a coincidental duplicate.
 */
export const SWAP_MOTION = {
  transition:
    'transition-[opacity,scale,filter] duration-200 ease-swap motion-reduce:transition-[opacity]',
  shown: 'opacity-100 blur-[0px] scale-100',
  hidden: 'opacity-0 blur-[4px] scale-[0.97] motion-reduce:blur-[0px] motion-reduce:scale-100',
} as const;

/**
 * Fallback slack past the exit duration before `onDismiss` fires anyway —
 * covers environments where no transitionend ever arrives (reduced motion,
 * jsdom, display:none ancestors).
 */
const EXIT_FALLBACK_SLACK_MS = 50;

interface OwnedExit {
  /** True once the exit has begun — callers gate their tap handlers on it. */
  leaving: boolean;
  /** Start the exit transition (idempotent — later calls are no-ops). */
  beginExit: () => void;
  /** Attach to the transitioning root; fires `onDismiss` once on completion. */
  onTransitionEnd: (event: TransitionEvent<HTMLElement>) => void;
}

/**
 * Component-owned exit (§4.1): a ✕ tap first plays the element's own exit
 * transition, then reports the dismissal, so consumers can unmount on
 * `onDismiss` without any animation machinery of their own. Fires exactly
 * once — on the root's opacity transitionend, or on a timer fallback when
 * transitions never run. `onExitStart` (optional) fires synchronously the
 * moment the exit begins — the overlap hook for fold choreography.
 */
export function useOwnedExit(
  onDismiss: () => void,
  exitMs: number,
  onExitStart?: () => void,
): OwnedExit {
  const [leaving, setLeaving] = useState(false);
  const exitStartedRef = useRef(false);
  const firedRef = useRef(false);
  // Latest-callback ref: the fallback timer must neither reset nor call a
  // stale callback when a parent re-renders with a new inline onDismiss
  // mid-exit — with it, the timer effect's deps stay stable.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  });

  useEffect(() => {
    if (!leaving) {
      return undefined;
    }
    const id = window.setTimeout(() => {
      if (!firedRef.current) {
        firedRef.current = true;
        onDismissRef.current();
      }
    }, exitMs + EXIT_FALLBACK_SLACK_MS);
    return () => {
      window.clearTimeout(id);
    };
  }, [leaving, exitMs]);

  const beginExit = () => {
    if (exitStartedRef.current) {
      return;
    }
    exitStartedRef.current = true;
    onExitStart?.();
    setLeaving(true);
  };

  const onTransitionEnd = (event: TransitionEvent<HTMLElement>) => {
    if (!leaving || event.target !== event.currentTarget || event.propertyName !== 'opacity') {
      return;
    }
    if (!firedRef.current) {
      firedRef.current = true;
      onDismissRef.current();
    }
  };

  return { leaving, beginExit, onTransitionEnd };
}

/**
 * Enter-transition trigger: mounts in the enter pose, then flips to settled
 * one frame later so the CSS transition actually animates (NewPostsPill's
 * rAF machinery). A positive `delayMs` waits that long instead — used to
 * delay only the ENTER (the card→pill fold stagger) while leaving exit and
 * retarget timing untouched. `startSettled` is a lazy initializer: return
 * true to skip the animation and render settled from the first frame.
 */
export function useEnterSettle(startSettled: () => boolean, delayMs = 0): boolean {
  const [settled, setSettled] = useState(startSettled);

  useEffect(() => {
    if (settled) {
      return undefined;
    }
    if (delayMs > 0) {
      const id = window.setTimeout(() => {
        setSettled(true);
      }, delayMs);
      return () => {
        window.clearTimeout(id);
      };
    }
    const id = requestAnimationFrame(() => {
      setSettled(true);
    });
    return () => {
      cancelAnimationFrame(id);
    };
  }, [settled, delayMs]);

  return settled;
}
